import { useCallback, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";
import { Icon } from "./icons";
import s from "./popover.module.css";

export interface PopoverProps {
  /** Renders the trigger. Spread `triggerProps` on a button. */
  trigger: (triggerProps: ButtonHTMLAttributes<HTMLButtonElement> & { ref: (el: HTMLButtonElement | null) => void }, state: { open: boolean }) => ReactNode;
  /** Receives `close` so a menu item can close the popover and return focus. */
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  role?: "menu" | "dialog" | "listbox";
  label: string;
  align?: "start" | "end";
  className?: string;
  popoverClassName?: string;
  minWidth?: number;
  /** Controlled mode, optional. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A dropdown anchored to its trigger: closes on Esc, outside press and Tab out, returns focus to the trigger,
 * and on a phone becomes a full-width sheet under the top bar (CSS). Arrow keys move through `[role=menuitem]`.
 */
export function Popover({ trigger, children, role = "dialog", label, align = "end", className, popoverClassName, minWidth, open: controlled, onOpenChange }: PopoverProps) {
  const [inner, setInner] = useState(false);
  const open = controlled ?? inner;
  const setOpen = useCallback((next: boolean) => { setInner(next); onOpenChange?.(next); }, [onOpenChange]);
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, [setOpen]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, setOpen]);

  useEffect(() => {
    if (open && role === "menu") boxRef.current?.querySelector<HTMLElement>("[role=menuitem]:not(:disabled)")?.focus();
  }, [open, role]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.stopPropagation(); close(); return; }
    if (e.key === "Tab" && !boxRef.current?.contains(document.activeElement)) return;
    if (role === "menu" && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      const items = [...(boxRef.current?.querySelectorAll<HTMLElement>("[role=menuitem]:not(:disabled)") ?? [])];
      if (items.length === 0) return;
      e.preventDefault();
      const at = items.indexOf(document.activeElement as HTMLElement);
      const next = e.key === "ArrowDown" ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
      items[next]?.focus();
    }
  };

  return (
    <div className={cx(s.wrap, className)} ref={wrapRef} onKeyDown={onKeyDown}>
      {trigger(
        {
          ref: (el) => { triggerRef.current = el; },
          onClick: () => setOpen(!open),
          "aria-haspopup": role === "menu" ? "menu" : role === "listbox" ? "listbox" : "dialog",
          "aria-expanded": open,
          "aria-controls": open ? id : undefined,
        },
        { open },
      )}
      {open ? (
        <div id={id} ref={boxRef} role={role} aria-label={label} className={cx(s.pop, align === "start" && s.left, popoverClassName)} style={minWidth ? { minWidth } : undefined}>
          {typeof children === "function" ? children({ close: () => close() }) : children}
        </div>
      ) : null}
    </div>
  );
}

export interface PopoverItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  role?: "menuitem" | "option";
  selected?: boolean;
  /** Leading content, usually an icon. */
  lead?: ReactNode;
  hint?: ReactNode;
}

/** A row in a popover menu or listbox. */
export function PopoverItem({ role = "menuitem", selected, lead, hint, children, className, ...rest }: PopoverItemProps) {
  return (
    <button type="button" role={role} aria-selected={role === "option" ? !!selected : undefined} className={cx(s.item, className)} {...rest}>
      {lead}
      <span>{children}{hint ? <small>{hint}</small> : null}</span>
      {selected ? <Icon name="check" className={s.check} /> : null}
    </button>
  );
}

export function PopoverHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <h4>{children}{action}</h4>;
}

export const PopoverRule = () => <hr />;
